import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { IMAGES, TREATMENT_VIDEOS, buildWhatsAppUrl } from "@/lib/site";
import { treatmentsCatalog } from "@/data/catalog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import SmartBgVideo from "@/components/SmartBgVideo";
import MapSection from "@/components/MapSection";

const Home = () => {
  const { t, lang } = useLang();
  const waUrl = buildWhatsAppUrl("first_visit", lang);

  const bento = [
    { key: "invisalign", span: "md:col-span-7 md:row-span-2" },
    { key: "implants", span: "md:col-span-5" },
    { key: "design", span: "md:col-span-5" },
  ];
  const findTr = (k) => treatmentsCatalog.find((c) => c.key === k);

  return (
    <main data-testid="home-page" className="bg-cream text-ink">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[100svh] bg-ink text-cream overflow-hidden grain">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.heroBg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/40 to-ink" />
        </div>

        <div aria-hidden className="absolute -right-[10%] top-1/2 -translate-y-1/2 font-serif text-[60vw] md:text-[42vw] leading-none text-cream/[0.04] select-none pointer-events-none">
          M
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-40 pb-24 min-h-[100svh] flex flex-col justify-between">
          <div>
            <p className="overline text-cream/70 fade-up">{t.hero.eyebrow}</p>
            <h1 className="mt-8 font-serif font-light leading-[0.92] tracking-tight text-[clamp(3.4rem,11vw,11rem)] max-w-[10ch]">
              <span className="block fade-up fade-up-delay-1">{t.hero.title_1}</span>
              <span className="block italic fade-up fade-up-delay-2">{t.hero.title_2}</span>
              <span className="block fade-up fade-up-delay-3 text-cream/70">{t.hero.title_3}</span>
            </h1>

            <p className="mt-10 max-w-xl text-cream/80 font-light text-lg leading-relaxed fade-up fade-up-delay-4">
              {t.hero.lede}
            </p>

            <div className="mt-10 flex flex-wrap gap-4 fade-up fade-up-delay-4">
              <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-primary" className="px-8 py-4 bg-cream text-ink text-xs tracking-[0.25em] uppercase btn-sweep border border-cream hover:text-cream">
                {t.hero.cta_primary}
              </a>
              <Link to="/tratamientos" data-testid="hero-cta-secondary" className="px-8 py-4 border border-cream/40 text-cream text-xs tracking-[0.25em] uppercase btn-sweep sweep-cream hover:text-ink">
                {t.hero.cta_secondary}
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-end justify-between mt-20 pt-10 border-t border-cream/15">
            <div>
              <p className="overline text-cream/60">{t.hero.tag_left}</p>
              <p className="font-serif text-3xl mt-2">{t.hero.tag_left_strong}</p>
            </div>
            <div className="flex items-center gap-2 text-cream/60 text-xs tracking-[0.3em] uppercase">
              <ArrowDown size={14} />
              {t.hero.scroll}
            </div>
            <div className="text-right">
              <p className="font-serif italic text-2xl">imayodental.es</p>
              <p className="overline text-cream/60 mt-1">Cerdanyola del Vallès</p>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-ink text-cream py-6 border-y border-cream/10 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap gap-16 font-serif text-2xl italic">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-16 px-8">
              {["Invisalign", "Implantología", "Diseño de Sonrisa", "Mayo Kids", "Periodoncia", "Endodoncia", "Estética", "Prótesis"].map((w, i) => (
                <span key={i} className="flex items-center gap-16">
                  <span>{w}</span>
                  <span className="text-cream/40">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section data-testid="philosophy-section" className="py-28 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <p className="overline">{t.philosophy.eyebrow}</p>
            <div className="mt-6 h-px w-16 bg-ink/40" />
          </div>
          <div className="md:col-span-8">
            <blockquote className="font-serif text-3xl md:text-5xl leading-[1.1] font-light tracking-tight">
              <span className="text-ink/30 font-serif italic mr-3">“</span>
              {t.philosophy.quote}
              <span className="text-ink/30 font-serif italic ml-2">”</span>
            </blockquote>
            <p className="mt-10 max-w-xl text-ink/70 font-light leading-relaxed">{t.philosophy.body}</p>
            <p className="mt-6 font-serif italic text-xl">{t.philosophy.sign}</p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="bg-ink text-cream py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-6">
          {[
            { num: t.stats.a_num, label: t.stats.a_label },
            { num: t.stats.b_num, label: t.stats.b_label },
            { num: t.stats.c_num, label: t.stats.c_label },
            { num: t.stats.d_num, label: t.stats.d_label },
          ].map((s, i) => (
            <div key={i} data-testid={`stat-${i}`} className="border-l border-cream/20 pl-6 md:pl-8">
              <p className="font-serif text-5xl md:text-7xl font-light tracking-tight">{s.num}</p>
              <p className="mt-3 overline text-cream/60">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TREATMENTS HIGHLIGHTS */}
      <section data-testid="treatments-highlights" className="py-28 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="overline">{t.treatments.eyebrow}</p>
              <h2 className="mt-4 font-serif text-5xl md:text-6xl font-light tracking-tight max-w-[18ch] leading-[1.05]">
                {t.treatments.title}
              </h2>
            </div>
            <Link to="/tratamientos" data-testid="treatments-cta-all" className="inline-flex items-center gap-3 px-6 py-3 border border-ink text-ink text-xs tracking-[0.25em] uppercase btn-sweep hover:text-cream">
              {t.treatments.cta_all}
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-12 gap-6 md:gap-8 auto-rows-[280px] md:auto-rows-[300px]">
            {bento.map((it) => {
              const tr = findTr(it.key);
              const d = tr[lang];
              const videoSrc = TREATMENT_VIDEOS[it.key];
              return (
                <Link
                  key={it.key}
                  to={`/tratamientos#${it.key}`}
                  data-testid={`treatment-card-${it.key}`}
                  className={`${it.span} relative group overflow-hidden bg-ink`}
                >
                  {videoSrc ? (
                    <SmartBgVideo
                      src={videoSrc}
                      poster={tr.img}
                      alt={d.name}
                      className="absolute inset-0 w-full h-full opacity-80 group-hover:opacity-100 transition-opacity duration-700"
                      imgClassName="group-hover:scale-105 transition-transform duration-700"
                      videoClassName="group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <img src={tr.img} alt={d.name} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
                  <div className="relative h-full p-8 flex flex-col justify-end text-cream">
                    <p className="overline text-cream/70">{d.sub}</p>
                    <h3 className="font-serif text-3xl md:text-4xl font-light mt-2">{d.name}</h3>
                    <p className="mt-3 max-w-md font-light text-cream/80 text-sm leading-relaxed">{d.lede}</p>
                    <div className="mt-4 flex items-center gap-2 text-xs tracking-[0.25em] uppercase">
                      <span>→</span> {t.treatments.learn_more}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* DOCTOR */}
      <section data-testid="doctor-section" className="bg-cream-dark/40 py-28 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <div className="aspect-[3/4] bg-ink overflow-hidden">
              <img src={IMAGES.doctorPortrait} alt={t.doctor.name} className="w-full h-full object-cover object-center" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block font-serif text-9xl text-ink/10 italic pointer-events-none">M</div>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <p className="overline">{t.doctor.eyebrow}</p>
            <h2 className="mt-6 font-serif text-5xl md:text-6xl font-light tracking-tight leading-[1.05] max-w-[18ch]">{t.doctor.title}</h2>
            <p className="mt-8 text-ink/80 font-light leading-relaxed max-w-xl">{t.doctor.bio_1}</p>
            <p className="mt-4 text-ink/80 font-light leading-relaxed max-w-xl">{t.doctor.bio_2}</p>
            <div className="mt-10 max-w-md border-t border-ink/20 pt-6">
              <p className="font-serif italic text-3xl">{t.doctor.sign}</p>
              <p className="overline text-ink/60 mt-1">{t.doctor.name}</p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="bg-ink text-cream py-28 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="overline text-cream/60">{t.testimonials.eyebrow}</p>
              <h2 className="mt-4 font-serif text-5xl md:text-6xl font-light tracking-tight max-w-[20ch] leading-[1.05]">{t.testimonials.title}</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {t.testimonials.items.map((it, i) => (
              <figure key={i} data-testid={`testimonial-${i}`} className="border-t border-cream/20 pt-8">
                <span className="font-serif text-6xl leading-none text-cream/30">“</span>
                <blockquote className="font-serif text-xl md:text-2xl font-light leading-snug mt-2">{it.quote}</blockquote>
                <figcaption className="mt-8 overline text-cream/70">{it.name} · <span className="opacity-60">{it.city}</span></figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-28 md:py-36">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline">{t.faq.eyebrow}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">{t.faq.title}</h2>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {t.faq.items.map((it, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-ink/15">
                  <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-serif text-xl md:text-2xl font-light py-6 hover:no-underline">
                    {it.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink/75 font-light leading-relaxed pb-6">{it.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <MapSection variant="light" />
    </main>
  );
};

export default Home;
